import { Form, Formik, FormikHelpers } from "formik";
import Layout from "../../components/Layout";
import {
  Button,
  capitalize,
  Chip,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from "@mui/material";
import * as yup from "yup";
import React, { useCallback } from "react";
import { useNavigate, useParams } from "react-router";
import SubmitButton from "../../components/SubmitButton";
import { useTranslation } from "react-i18next";
import {
  useApproveAnwesenheit,
  useUpdateAnwesenheit,
} from "../../client/hooks";
import { useMutation, useQueryClient } from "react-query";
import { AnwesenheitDaten, Benutzer } from "../../api";
import AnwesenheitFieldsContent from "../../components/anwesenheiten/AnwesenheitFieldsContent";
import { useAnwesenheit, useBenutzer } from "../../client/queries";
import DeleteAnwesenheitButton from "../../components/anwesenheiten/DeleteAnwesenheitButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorAlert from "../../components/ErrorAlert";
import { notPermitted } from "../../errors";
import Guard, { usePermitted } from "../../components/Guard";

export default function DetailsAnwesenheit() {
  const { t } = useTranslation("customer");
  const { id: anwesenheitId = "" } = useParams<{
    id: string;
  }>();
  const queryClient = useQueryClient();
  const anwesenheit = useAnwesenheit(anwesenheitId);
  const benutzer = useBenutzer(anwesenheit.mitarbeiterId);
  const { mutateAsync } = useSaveAnwesenheit(anwesenheitId);
  const approveAnwesenheit = useApproveAnwesenheit();
  const permittedForUpdateAfterwards: boolean = usePermitted({
    permission: "anwesenheit:edit:afterwards",
  });

  let schema = yup.object().shape({
    datum: yup
      .date()
      .required("Datum ist erforderlich!")
      .max(
        new Date("9999-12-31"),
        "Datum darf nicht über dem 31.12.9999 liegen!"
      ),
    start: yup.string(),
    ende: yup.string(),
    bezahltePause: yup.number(),
    unbezahltePause: yup.number(),
    fahrzeiten: yup.number(),
    arbeitszeitBrutto: yup.string(),
    arbeitszeitNetto: yup.string(),
    mitarbeiter: yup.object(),
    status: yup.string(),
    arbeitszeitKonto: yup.string(),
    brzUebertrag: yup.string(),
  });
  const navigate = useNavigate();

  const onSubmit = useCallback(
    async (
      values: {
        datum: string;
        start: string;
        ende: string;
        mitarbeiter: any;
        bezahltePause: number;
        unbezahltePause: number;
        fahrzeiten: number;
        arbeitszeitBrutto: string;
        arbeitszeitNetto: string;
        arbeitszeitKonto: number;
        brzUebertrag: string;
      },
      formikHelpers: FormikHelpers<{
        datum: string;
        start: string;
        ende: string;
        mitarbeiter: any;
        bezahltePause: number;
        unbezahltePause: number;
        fahrzeiten: number;
        arbeitszeitBrutto: string;
        arbeitszeitNetto: string;
        arbeitszeitKonto: number;
        brzUebertrag: string;
      }>
    ) => {
      console.log(anwesenheit.status);
      const { setSubmitting, setStatus } = formikHelpers;
      if (values.start !== "" && values.ende !== "") {
        const mitarbeiterId =
          typeof values.mitarbeiter === "string"
            ? values.mitarbeiter
            : (values.mitarbeiter as Benutzer).id ?? "";
        if (mitarbeiterId !== "") {
          await mutateAsync({
            datum: values.datum,
            mitarbeiterId: mitarbeiterId,
            start: values.start,
            ende: values.ende,
            bezahltePause: values.bezahltePause,
            unbezahltePause: values.unbezahltePause,
            fahrzeiten: values.fahrzeiten,
            arbeitszeitBrutto: parseFloat(
              values.arbeitszeitBrutto.replace(",", ".")
            ),
            arbeitszeitNetto: parseFloat(
              values.arbeitszeitNetto.replace(",", ".")
            ),
            status: anwesenheit.status,
            arbeitszeitKonto: values.arbeitszeitKonto,
            brzUebertrag: parseFloat(values.brzUebertrag.replace(",", ".")),
          });
        }
      } else {
        console.log("start", values.start);
        console.log("ende", values.ende);
      }
      setStatus(undefined);
      try {
      } catch (error: any) {
        setStatus(error.message);
      } finally {
        setSubmitting(false);
      }
    },
    [navigate, mutateAsync]
  );
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };
  const formatNumber = (value: number | undefined) => {
    // If the value is null, undefined, or NaN, return an empty string
    if (value == null || isNaN(value)) return "";

    // Format the number with exactly two decimal places
    return value
      .toFixed(2)
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };

  async function approve(values: {
    datum: string;
    start: string;
    ende: string;
    mitarbeiter: any;
    bezahltePause: number;
    unbezahltePause: number;
    fahrzeiten: number;
    arbeitszeitBrutto: string;
    arbeitszeitNetto: string;
    arbeitszeitKonto: number;
    brzUebertrag: string;
  }) {
    const mitarbeiterId =
      typeof values.mitarbeiter === "string"
        ? values.mitarbeiter
        : (values.mitarbeiter as Benutzer).id ?? "";
    await approveAnwesenheit(anwesenheitId, {
      datum: values.datum,
      mitarbeiterId: mitarbeiterId,
      start: values.start,
      ende: values.ende,
      bezahltePause: values.bezahltePause,
      unbezahltePause: values.unbezahltePause,
      fahrzeiten: values.fahrzeiten,
      arbeitszeitBrutto: parseFloat(values.arbeitszeitBrutto.replace(",", ".")),
      arbeitszeitNetto: parseFloat(values.arbeitszeitNetto.replace(",", ".")),
      status: "ANGENOMMEN",
      arbeitszeitKonto: values.arbeitszeitKonto,
      brzUebertrag: parseFloat(values.brzUebertrag.replace(",", ".")),
    });
    await queryClient.invalidateQueries(["anwesenheiten", anwesenheitId]);
    navigate(`/anwesenheiten`);
  }

  return (
    <Layout title={"Anwesenheit"} onBackClick={() => navigate(-1)}>
      <Guard
        permission={"anwesenheit:edit"}
        fallback={<ErrorAlert error={notPermitted()} />}
      >
        <Paper
          elevation={1}
          sx={{
            marginTop: 2,
            padding: 3,
            marginLeft: 2,
            marginRight: 2,
            position: "relative",
          }}
        >
          <Guard permission={"anwesenheit:delete"}>
            <DeleteAnwesenheitButton
              id={anwesenheitId}
            ></DeleteAnwesenheitButton>
          </Guard>
          <Formik
            initialValues={{
              datum: anwesenheit.datum ?? getCurrentDate(),
              start: anwesenheit.start,
              ende: anwesenheit.ende,
              mitarbeiter: {
                id: anwesenheit.mitarbeiterId,
                name: `${benutzer?.name}, ${benutzer?.vorname}`,
              },
              bezahltePause: anwesenheit.bezahltePause ?? 0,
              unbezahltePause: anwesenheit.unbezahltePause ?? 0,
              fahrzeiten: anwesenheit.fahrzeiten ?? 0,
              arbeitszeitBrutto:
                formatNumber(anwesenheit.arbeitszeitBrutto) ?? 0,
              arbeitszeitNetto: formatNumber(anwesenheit.arbeitszeitNetto) ?? 0,
              arbeitszeitKonto: anwesenheit.arbeitszeitKonto ?? 0,
              brzUebertrag: formatNumber(anwesenheit.brzUebertrag) ?? 0,
            }}
            validationSchema={schema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting, isValidating, isValid, dirty, values }) => (
              <>
                <Typography variant="h5">
                  {anwesenheit.geloeschtAm === undefined ? (
                    "Anwesenheit"
                  ) : (
                    <>
                      <>{"Anwesenheit "}</>
                      <Chip
                        size="small"
                        icon={<DeleteIcon />}
                        label={"gelöscht"}
                        color="error"
                      />
                    </>
                  )}
                </Typography>
                <DialogContent>
                  <Form id="new-anwesenheit">
                    <AnwesenheitFieldsContent
                      isVerwaltungAnwesenheit={true}
                      startzeit={values.start}
                      endzeit={values.ende}
                    />
                  </Form>
                </DialogContent>
                <DialogActions>
                  <Button
                    type="button"
                    variant="contained"
                    color="success"
                    onClick={() => approve(values)}
                    disabled={
                      isValidating || !isValid || anwesenheit.status !== "OFFEN"
                    }
                  >
                    Speichern & Freigeben
                  </Button>
                  <SubmitButton
                    form="new-anwesenheit"
                    type="submit"
                    variant="contained"
                    color="secondary"
                    loading={isSubmitting}
                    disabled={
                      isValidating ||
                      !isValid ||
                      !dirty ||
                      !(
                        anwesenheit.status !== "OFFEN" &&
                        permittedForUpdateAfterwards
                      )
                    }
                  >
                    {capitalize(t("save"))}
                  </SubmitButton>
                </DialogActions>
              </>
            )}
          </Formik>
        </Paper>
      </Guard>
    </Layout>
  );
}

function useSaveAnwesenheit(id: string) {
  const updatedAnwesenheit = useUpdateAnwesenheit();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation(
    (input: AnwesenheitDaten) => {
      return updatedAnwesenheit(id, input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["anwesenheiten", id]);
        navigate(`/anwesenheiten`);
      },
    }
  );
}
